import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database, storage } from '../../firebase';
import { ref, get, set, push, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import {
  PlusIcon, TrashIcon, PencilIcon, XMarkIcon,
  PhotoIcon, ArrowUpTrayIcon, SparklesIcon,
  EyeIcon, EyeSlashIcon, FolderIcon,
  TagIcon, CalendarIcon, UserIcon,
  DocumentTextIcon, FilmIcon, MagnifyingGlassIcon,
  CheckCircleIcon, ClockIcon, GlobeAltIcon,
  ArchiveBoxIcon, PencilSquareIcon, BookOpenIcon,
} from '@heroicons/react/24/outline';

// ─── SWAL Z-INDEX ─────────────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'swal-topz' } };
if (typeof document !== 'undefined' && !document.getElementById('swal-topz-style')) {
  const s = document.createElement('style');
  s.id = 'swal-topz-style';
  s.textContent = '.swal-topz{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { key: 'draft',     label: 'Draft',     icon: <PencilSquareIcon className="h-4 w-4" />, color: '#d97706', bg: '#fef3c7' },
  { key: 'published', label: 'Published', icon: <GlobeAltIcon     className="h-4 w-4" />, color: '#16a34a', bg: '#dcfce7' },
  { key: 'archived',  label: 'Archived',  icon: <ArchiveBoxIcon   className="h-4 w-4" />, color: '#6b7280', bg: '#f3f4f6' },
];

const EMPTY_BLOG = {
  title:        '',
  excerpt:      '',
  categoryId:   '',
  tags:         [],
  author:       '',
  status:       'draft',
  featured:     false,
  coverImage:   '',
  photos:       [],
  videoUrl:     '',
  videoFile:    '',
  description:  [],
  seoTitle:     '',
  seoDesc:      '',
  readTime:     '',
};

const EMPTY_CATEGORY = { name: '', color: '#800020', description: '' };

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const estimateReadTime = (sections) => {
  const words = sections.reduce((acc, s) => acc + ((s.title || '') + ' ' + (s.content || '')).split(/\s+/).length, 0);
  return Math.max(1, Math.ceil(words / 200));
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const BlogManagement = () => {
  const { userData, currentUser } = useAuth();

  const userRole     = (userData?.personalInfo?.role || '').toLowerCase();
  const isAdmin      = userRole === 'admin';
  const isSuperAdmin = userRole === 'superadmin';
  const isAuthorized = isAdmin || isSuperAdmin;

  const requireAuth = () => {
    if (!isAuthorized) {
      toast('error', 'Access Denied', 'Only Admins and Super-Admins can perform this action.');
      return false;
    }
    return true;
  };

  const [blogs,           setBlogs]           = useState([]);
  const [categories,      setCategories]      = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [filterStatus,    setFilterStatus]    = useState('all');
  const [filterCategory,  setFilterCategory]  = useState('all');

  const [isBlogModalOpen, setIsBlogModalOpen] = useState(false);
  const [isCatModalOpen,  setIsCatModalOpen]  = useState(false);
  const [editingBlog,     setEditingBlog]     = useState(null);
  const [blogForm,        setBlogForm]        = useState(EMPTY_BLOG);
  const [uploading,       setUploading]       = useState(false);
  const [uploadingVideo,  setUploadingVideo]  = useState(false);

  useEffect(() => { fetchBlogs(); fetchCategories(); }, []);

  // ─── FETCH ────────────────────────────────────────────────────────────────
  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const snap = await get(ref(database, 'blogs'));
      setBlogs(snap.exists()
        ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v }))
        : []);
    } catch (e) { console.error(e); toast('error', 'Error', 'Failed to load blogs'); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const snap = await get(ref(database, 'blogCategories'));
      setCategories(snap.exists()
        ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v }))
        : []);
    } catch (e) { console.error(e); }
  };

  // ─── BLOG CRUD ────────────────────────────────────────────────────────────
  const openAdd = () => {
    if (!requireAuth()) return;
    setEditingBlog(null);
    setBlogForm({ ...EMPTY_BLOG, description: [], photos: [], tags: [] });
    setIsBlogModalOpen(true);
  };

  const openEdit = (blog) => {
    if (!requireAuth()) return;
    setEditingBlog(blog);
    setBlogForm({
      title:       blog.title       || '',
      excerpt:     blog.excerpt     || '',
      categoryId:  blog.categoryId  || '',
      tags:        blog.tags        || [],
      author:      blog.author      || '',
      status:      blog.status      || 'draft',
      featured:    blog.featured    || false,
      coverImage:  blog.coverImage  || '',
      photos:      blog.photos      || [],
      videoUrl:    blog.videoUrl    || '',
      videoFile:   blog.videoFile   || '',
      description: blog.description || [],
      seoTitle:    blog.seoTitle    || '',
      seoDesc:     blog.seoDesc     || '',
      readTime:    blog.readTime    || '',
    });
    setIsBlogModalOpen(true);
  };

  const saveBlog = async (e) => {
    e.preventDefault();
    if (!blogForm.title.trim()) return toast('error', 'Validation Error', 'Blog title is required');
    if (blogForm.description.length === 0) return toast('error', 'Validation Error', 'Add at least one content section');

    setLoading(true);
    try {
      const now = Date.now();
      const autoReadTime = estimateReadTime(blogForm.description);
      const payload = {
        title:       blogForm.title.trim(),
        slug:        editingBlog?.slug || slugify(blogForm.title),
        excerpt:     blogForm.excerpt.trim(),
        categoryId:  blogForm.categoryId,
        tags:        blogForm.tags,
        author:      blogForm.author.trim(),
        status:      blogForm.status,
        featured:    blogForm.featured,
        coverImage:  blogForm.coverImage,
        photos:      blogForm.photos,
        videoUrl:    blogForm.videoUrl.trim(),
        videoFile:   blogForm.videoFile,
        description: blogForm.description,
        seoTitle:    blogForm.seoTitle.trim() || blogForm.title.trim(),
        seoDesc:     blogForm.seoDesc.trim()  || blogForm.excerpt.trim(),
        readTime:    blogForm.readTime || autoReadTime,
        updatedAt:   now,
        createdAt:   editingBlog ? (editingBlog.createdAt || now) : now,
        publishedAt: blogForm.status === 'published'
          ? (editingBlog?.publishedAt || now)
          : (editingBlog?.publishedAt || null),
        createdBy:     currentUser?.uid || '',
        createdByName: userData?.personalInfo?.name || 'System',
      };

      if (editingBlog) {
        await update(ref(database, `blogs/${editingBlog.id}`), payload);
        toast('success', 'Updated!', 'Blog updated successfully');
      } else {
        const nr = push(ref(database, 'blogs'));
        await set(nr, { ...payload, id: nr.key });
        toast('success', 'Created!', `Blog ${payload.status === 'published' ? 'published' : 'saved as draft'}`);
      }
      setIsBlogModalOpen(false);
      fetchBlogs();
    } catch (err) {
      console.error(err);
      toast('error', 'Error', 'Failed to save blog');
    } finally { setLoading(false); }
  };

  const deleteBlog = async (blog) => {
    if (!requireAuth()) return;
    const ok = await Swal.fire({
      title: `Delete "${blog.title}"?`,
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#800020',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      ...SWAL_Z,
    });
    if (!ok.isConfirmed) return;
    await remove(ref(database, `blogs/${blog.id}`));
    toast('success', 'Deleted');
    fetchBlogs();
  };

  const toggleFeatured = async (blog) => {
    if (!requireAuth()) return;
    await update(ref(database, `blogs/${blog.id}`), { featured: !blog.featured });
    fetchBlogs();
    toast('success', blog.featured ? 'Removed from featured' : 'Marked as featured');
  };

  const changeStatus = async (blog, status) => {
    if (!requireAuth()) return;
    const updates = { status };
    if (status === 'published' && !blog.publishedAt) updates.publishedAt = Date.now();
    await update(ref(database, `blogs/${blog.id}`), updates);
    fetchBlogs();
    toast('success', `Status changed to ${status}`);
  };

  // ─── UPLOADS ─────────────────────────────────────────────────────────────
  const uploadPhotos = async (files, isCover = false) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const sr = storageRef(storage, `blogs/${Date.now()}_${f.name}`);
        await uploadBytes(sr, f);
        urls.push(await getDownloadURL(sr));
      }
      if (isCover) {
        setBlogForm(p => ({ ...p, coverImage: urls[0] }));
      } else {
        setBlogForm(p => ({ ...p, photos: [...p.photos, ...urls] }));
      }
    } catch (e) { toast('error', 'Upload failed'); }
    finally { setUploading(false); }
  };

  const uploadVideo = async (file) => {
    setUploadingVideo(true);
    try {
      const sr = storageRef(storage, `blogs/videos/${Date.now()}_${file.name}`);
      await uploadBytes(sr, file);
      const url = await getDownloadURL(sr);
      setBlogForm(p => ({ ...p, videoFile: url, videoUrl: '' }));
      toast('success', 'Video uploaded');
    } catch (e) { toast('error', 'Video upload failed'); }
    finally { setUploadingVideo(false); }
  };

  // ─── FILTER ───────────────────────────────────────────────────────────────
  const filtered = blogs.filter(b => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || b.title?.toLowerCase().includes(q) || b.author?.toLowerCase().includes(q) || b.tags?.some(t => t.toLowerCase().includes(q));
    const matchStatus = filterStatus   === 'all' || b.status   === filterStatus;
    const matchCat    = filterCategory === 'all' || b.categoryId === filterCategory;
    return matchSearch && matchStatus && matchCat;
  });

  const stats = {
    total:     blogs.length,
    published: blogs.filter(b => b.status === 'published').length,
    draft:     blogs.filter(b => b.status === 'draft').length,
    featured:  blogs.filter(b => b.featured).length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
              {userRole && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                  style={{
                    background: isSuperAdmin ? '#1a1a2e' : isAdmin ? '#fef2f2' : '#f3f4f6',
                    color:      isSuperAdmin ? '#fff'    : isAdmin ? '#800020' : '#6b7280',
                    border:     `1px solid ${isSuperAdmin ? '#1a1a2e' : isAdmin ? '#fcd5de' : '#e5e7eb'}`,
                  }}>
                  {isSuperAdmin ? '⚡ Super Admin' : isAdmin ? '🛡 Admin' : '👁 Read Only'}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1">Create, manage and publish blog content</p>
          </div>
          {isAuthorized && (
            <div className="flex gap-3">
              <button onClick={() => setIsCatModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold border-2 transition-all text-sm"
                style={{ borderColor: '#800020', color: '#800020' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <FolderIcon className="h-4 w-4" />
                Categories
              </button>
              <button onClick={openAdd}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all text-sm"
                style={{ background: 'linear-gradient(135deg,#800020 0%,#b30000 100%)' }}>
                <PlusIcon className="h-5 w-5" />
                New Post
              </button>
            </div>
          )}
        </div>

        {/* ── STATS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Posts',  value: stats.total,     color: '#1a1a2e' },
            { label: 'Published',    value: stats.published, color: '#16a34a' },
            { label: 'Drafts',       value: stats.draft,     color: '#d97706' },
            { label: 'Featured',     value: stats.featured,  color: '#800020' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl px-5 py-4 border border-gray-100 shadow-sm">
              <p className="text-xs text-gray-400 font-medium">{s.label}</p>
              <p className="text-2xl font-bold mt-0.5" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* ── FILTERS ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by title, author or tag…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent shadow-sm"
              style={{ '--tw-ring-color': '#800020' }} />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 shadow-sm"
            style={{ '--tw-ring-color': '#800020' }}>
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 shadow-sm"
            style={{ '--tw-ring-color': '#800020' }}>
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* ── BLOG LIST ── */}
        {loading && blogs.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-block w-10 h-10 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3 text-sm">Loading posts…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <BookOpenIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">{blogs.length === 0 ? 'No posts yet' : 'No posts match your filters'}</p>
            {isAuthorized && blogs.length === 0 && (
              <button onClick={openAdd} className="mt-3 text-sm font-semibold" style={{ color: '#800020' }}>
                + Write your first post
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-gray-400 font-medium">{filtered.length} post{filtered.length !== 1 ? 's' : ''}</p>
            <AnimatePresence>
              {filtered.map(blog => (
                <BlogRow
                  key={blog.id}
                  blog={blog}
                  categories={categories}
                  isAuthorized={isAuthorized}
                  onEdit={() => openEdit(blog)}
                  onDelete={() => deleteBlog(blog)}
                  onToggleFeatured={() => toggleFeatured(blog)}
                  onStatusChange={(s) => changeStatus(blog, s)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ── BLOG MODAL ── */}
      <AnimatePresence>
        {isBlogModalOpen && (
          <BlogModal
            blogForm={blogForm}
            setBlogForm={setBlogForm}
            categories={categories}
            editing={!!editingBlog}
            loading={loading}
            uploading={uploading}
            uploadingVideo={uploadingVideo}
            uploadPhotos={uploadPhotos}
            uploadVideo={uploadVideo}
            onSubmit={saveBlog}
            onClose={() => setIsBlogModalOpen(false)}
            estimateReadTime={estimateReadTime}
          />
        )}
      </AnimatePresence>

      {/* ── CATEGORIES MODAL ── */}
      <AnimatePresence>
        {isCatModalOpen && (
          <CategoryModal
            categories={categories}
            onClose={() => setIsCatModalOpen(false)}
            onRefresh={fetchCategories}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BLOG ROW
// ═══════════════════════════════════════════════════════════════════════════════
const BlogRow = ({ blog, categories, isAuthorized, onEdit, onDelete, onToggleFeatured, onStatusChange }) => {
  const [statusOpen, setStatusOpen] = useState(false);
  const cat     = categories.find(c => c.id === blog.categoryId);
  const status  = STATUS_OPTIONS.find(s => s.key === blog.status) || STATUS_OPTIONS[0];
  const hasVideo = blog.videoUrl || blog.videoFile;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">

      <div className="flex items-start gap-4 p-5">
        {/* Cover thumbnail */}
        <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-gray-100">
          {blog.coverImage
            ? <img src={blog.coverImage} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center">
                <DocumentTextIcon className="h-8 w-8 text-gray-300" />
              </div>
          }
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-bold text-gray-900 text-base truncate">{blog.title}</h3>
                {blog.featured && (
                  <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ background: '#fef2f2', color: '#800020' }}>⭐ Featured</span>
                )}
                {hasVideo && (
                  <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-600">
                    <FilmIcon className="h-3 w-3 inline mr-0.5" />Video
                  </span>
                )}
              </div>
              {blog.excerpt && <p className="text-sm text-gray-500 line-clamp-1 mb-2">{blog.excerpt}</p>}
              <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
                {cat && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: cat.color || '#800020' }} />
                    {cat.name}
                  </span>
                )}
                {blog.author && <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" />{blog.author}</span>}
                {blog.readTime && <span className="flex items-center gap-1"><ClockIcon className="h-3 w-3" />{blog.readTime} min read</span>}
                {blog.tags?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <TagIcon className="h-3 w-3" />
                    {blog.tags.slice(0, 3).join(', ')}{blog.tags.length > 3 ? ` +${blog.tags.length - 3}` : ''}
                  </span>
                )}
                <span><CalendarIcon className="h-3 w-3 inline mr-0.5" />
                  {new Date(blog.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Status badge + dropdown */}
              <div className="relative">
                <button onClick={() => setStatusOpen(o => !o)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: status.bg, color: status.color }}>
                  {status.icon}
                  {status.label}
                </button>
                <AnimatePresence>
                  {statusOpen && isAuthorized && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                      className="absolute right-0 top-9 z-20 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[140px]">
                      {STATUS_OPTIONS.filter(s => s.key !== blog.status).map(s => (
                        <button key={s.key}
                          onClick={() => { onStatusChange(s.key); setStatusOpen(false); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold hover:bg-gray-50 transition-colors text-left"
                          style={{ color: s.color }}>
                          {s.icon} Set {s.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isAuthorized && (
                <>
                  <button onClick={onToggleFeatured} title={blog.featured ? 'Remove from featured' : 'Mark as featured'}
                    className="w-8 h-8 flex items-center justify-center rounded-lg transition-all"
                    style={{ background: blog.featured ? '#fef2f2' : '#f3f4f6', color: blog.featured ? '#800020' : '#9ca3af' }}>
                    <SparklesIcon className="h-4 w-4" />
                  </button>
                  <button onClick={onEdit}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-all">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button onClick={onDelete}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-red-50 text-gray-500 hover:text-red-600 transition-all">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BLOG MODAL
// ═══════════════════════════════════════════════════════════════════════════════
const BlogModal = ({
  blogForm, setBlogForm, categories,
  editing, loading, uploading, uploadingVideo,
  uploadPhotos, uploadVideo,
  onSubmit, onClose, estimateReadTime,
}) => {
  const coverRef  = useRef();
  const photoRef  = useRef();
  const videoRef  = useRef();

  // ── Description state ─────────────────────────────────────────────────────
  const [descTitle,      setDescTitle]      = useState('');
  const [descContent,    setDescContent]    = useState('');
  const [editingDescIdx, setEditingDescIdx] = useState(null);

  // ── Tags state ────────────────────────────────────────────────────────────
  const [tagInput, setTagInput] = useState('');

  // ── Description handlers ──────────────────────────────────────────────────
  const addDescSection = () => {
    if (!descTitle.trim() && !descContent.trim())
      return toast('error', 'Validation', 'Add a title, content, or both');
    setBlogForm(p => ({
      ...p,
      description: [...p.description, { title: descTitle.trim(), content: descContent.trim() }],
    }));
    setDescTitle(''); setDescContent(''); setEditingDescIdx(null);
  };

  const updateDescSection = () => {
    if (!descTitle.trim() && !descContent.trim())
      return toast('error', 'Validation', 'Add a title, content, or both');
    const updated = [...blogForm.description];
    updated[editingDescIdx] = { title: descTitle.trim(), content: descContent.trim() };
    setBlogForm(p => ({ ...p, description: updated }));
    setDescTitle(''); setDescContent(''); setEditingDescIdx(null);
  };

  const editDescSection = (idx) => {
    setDescTitle(blogForm.description[idx].title);
    setDescContent(blogForm.description[idx].content);
    setEditingDescIdx(idx);
  };

  const removeDescSection = (idx) =>
    setBlogForm(p => ({ ...p, description: p.description.filter((_, i) => i !== idx) }));

  const moveDesc = (idx, dir) => {
    const arr = [...blogForm.description];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setBlogForm(p => ({ ...p, description: arr }));
  };

  // ── Tag handlers ──────────────────────────────────────────────────────────
  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || blogForm.tags.includes(t)) return setTagInput('');
    setBlogForm(p => ({ ...p, tags: [...p.tags, t] }));
    setTagInput('');
  };

  const removeTag = (idx) =>
    setBlogForm(p => ({ ...p, tags: p.tags.filter((_, i) => i !== idx) }));

  const autoReadTime = estimateReadTime(blogForm.description);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-xl font-bold text-white">{editing ? 'Edit Post' : 'New Blog Post'}</h2>
            <p className="text-white/60 text-sm mt-0.5">Fill in the post details below</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="max-w-5xl mx-auto px-8 py-8 space-y-8">

            {/* ── BASIC INFO ── */}
            <Section title="Basic Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Label>Post Title *</Label>
                  <input
                    type="text"
                    value={blogForm.title}
                    onChange={e => setBlogForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g., 5 Benefits of Morning Yoga"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Excerpt / Short Description</Label>
                  <textarea
                    value={blogForm.excerpt}
                    onChange={e => setBlogForm(p => ({ ...p, excerpt: e.target.value }))}
                    rows={2}
                    placeholder="A short summary shown in blog listings…"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <div>
                  <Label>Author</Label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={blogForm.author}
                      onChange={e => setBlogForm(p => ({ ...p, author: e.target.value }))}
                      placeholder="e.g., John Kamau"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': '#800020' }}
                    />
                  </div>
                </div>
                <div>
                  <Label>Category</Label>
                  <select
                    value={blogForm.categoryId}
                    onChange={e => setBlogForm(p => ({ ...p, categoryId: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
                    style={{ '--tw-ring-color': '#800020' }}>
                    <option value="">Select a category…</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Read Time (minutes) <span className="font-normal text-gray-400 normal-case">— auto: ~{autoReadTime} min</span></Label>
                  <div className="relative">
                    <ClockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      value={blogForm.readTime}
                      onChange={e => setBlogForm(p => ({ ...p, readTime: e.target.value }))}
                      placeholder={`Auto: ${autoReadTime}`}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': '#800020' }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Leave blank to auto-calculate from content</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="flex gap-2">
                    {STATUS_OPTIONS.map(s => (
                      <button key={s.key} type="button"
                        onClick={() => setBlogForm(p => ({ ...p, status: s.key }))}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all"
                        style={{
                          borderColor: blogForm.status === s.key ? s.color : '#e5e7eb',
                          background:  blogForm.status === s.key ? s.bg  : '#fff',
                          color:       blogForm.status === s.key ? s.color : '#6b7280',
                        }}>
                        {s.icon} {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Featured toggle */}
              <div className="flex items-center gap-3 mt-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={blogForm.featured}
                    onChange={e => setBlogForm(p => ({ ...p, featured: e.target.checked }))}
                    className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-700" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {blogForm.featured ? '⭐ Featured post — highlighted on the blog page' : 'Mark as featured post'}
                  </span>
                </label>
              </div>
            </Section>

            {/* ── TAGS ── */}
            <Section title="Tags">
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } if (e.key === ',') { e.preventDefault(); addTag(); } }}
                    placeholder="Type tag and press Enter or comma…"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <button type="button" onClick={addTag}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white flex-shrink-0"
                  style={{ background: '#1a1a2e' }}>
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
              {blogForm.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {blogForm.tags.map((tag, i) => (
                    <span key={i}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border"
                      style={{ background: '#fef2f2', color: '#800020', borderColor: '#fcd5de' }}>
                      #{tag}
                      <button type="button" onClick={() => removeTag(i)} className="ml-0.5 hover:text-red-800">
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No tags added. Press Enter or comma after each tag.</p>
              )}
            </Section>

            {/* ── COVER IMAGE ── */}
            <Section title="Cover Image">
              {blogForm.coverImage ? (
                <div className="relative w-full rounded-2xl overflow-hidden border border-gray-200 group" style={{ height: 200 }}>
                  <img src={blogForm.coverImage} alt="Cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <label className="cursor-pointer w-10 h-10 bg-white rounded-full flex items-center justify-center shadow">
                      <input type="file" accept="image/*" className="hidden"
                        onChange={e => e.target.files[0] && uploadPhotos([e.target.files[0]], true)}
                        disabled={uploading} />
                      <PencilIcon className="h-4 w-4 text-gray-700" />
                    </label>
                    <button type="button" onClick={() => setBlogForm(p => ({ ...p, coverImage: '' }))}
                      className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center shadow text-white">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <span className="absolute bottom-2 left-2 text-xs text-white bg-black/50 px-2 py-0.5 rounded-full">Cover</span>
                </div>
              ) : (
                <label
                  className="flex flex-col items-center justify-center w-full rounded-2xl border-2 border-dashed cursor-pointer transition-all"
                  style={{ height: 160, borderColor: uploading ? '#800020' : '#d1d5db' }}
                  onMouseEnter={e => !uploading && (e.currentTarget.style.borderColor = '#800020')}
                  onMouseLeave={e => !uploading && (e.currentTarget.style.borderColor = '#d1d5db')}>
                  <input ref={coverRef} type="file" accept="image/*" className="hidden"
                    onChange={e => e.target.files[0] && uploadPhotos([e.target.files[0]], true)}
                    disabled={uploading} />
                  {uploading
                    ? <div className="w-8 h-8 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                    : <>
                        <PhotoIcon className="h-10 w-10 text-gray-300 mb-2" />
                        <p className="text-sm font-medium text-gray-500">Click to upload cover image</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP recommended</p>
                      </>
                  }
                </label>
              )}
            </Section>

            {/* ── CONTENT SECTIONS ── */}
            <Section title="Content Sections *">
              <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                <div>
                  <Label>Section Heading <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                  <input
                    type="text"
                    value={descTitle}
                    onChange={e => setDescTitle(e.target.value)}
                    placeholder="e.g., Introduction, Key Takeaways…"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <div>
                  <Label>Section Body <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                  <textarea
                    value={descContent}
                    onChange={e => setDescContent(e.target.value)}
                    rows={5}
                    placeholder="Write your content here…"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 transition-all bg-white"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  {editingDescIdx !== null && (
                    <button type="button"
                      onClick={() => { setDescTitle(''); setDescContent(''); setEditingDescIdx(null); }}
                      className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-all">
                      Cancel
                    </button>
                  )}
                  <button type="button"
                    onClick={editingDescIdx !== null ? updateDescSection : addDescSection}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                    style={{ background: editingDescIdx !== null ? '#800020' : '#1a1a2e' }}>
                    <PlusIcon className="h-4 w-4" />
                    {editingDescIdx !== null ? 'Update Section' : 'Add Section'}
                  </button>
                </div>
              </div>

              {blogForm.description.length > 0 ? (
                <div className="space-y-3">
                  {blogForm.description.map((sec, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-gray-400">#{i + 1}</span>
                            {sec.title && <p className="font-semibold text-gray-900 text-sm">{sec.title}</p>}
                          </div>
                          {sec.content && <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">{sec.content}</p>}
                          {!sec.title && !sec.content && <p className="text-sm text-gray-400 italic">Empty section</p>}
                        </div>
                        <div className="flex gap-1.5 ml-4 flex-shrink-0">
                          {i > 0 && (
                            <button type="button" onClick={() => moveDesc(i, -1)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs transition-all">↑</button>
                          )}
                          {i < blogForm.description.length - 1 && (
                            <button type="button" onClick={() => moveDesc(i, 1)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs transition-all">↓</button>
                          )}
                          <button type="button" onClick={() => editDescSection(i)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all">
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" onClick={() => removeDescSection(i)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all">
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No content sections yet. Use the editor above to add your first section.</p>
              )}
            </Section>

            {/* ── PHOTO GALLERY ── */}
            <Section title="Photo Gallery">
              <div className="flex flex-wrap gap-3">
                {blogForm.photos.map((url, i) => (
                  <div key={i} className="relative w-28 h-28 rounded-xl overflow-hidden border border-gray-200 group">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button type="button"
                        onClick={() => setBlogForm(p => ({ ...p, photos: p.photos.filter((_, idx) => idx !== i) }))}
                        className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <label
                  className="w-28 h-28 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all"
                  style={{ borderColor: uploading ? '#800020' : '#d1d5db' }}
                  onMouseEnter={e => !uploading && (e.currentTarget.style.borderColor = '#800020')}
                  onMouseLeave={e => !uploading && (e.currentTarget.style.borderColor = '#d1d5db')}>
                  <input ref={photoRef} type="file" multiple accept="image/*" className="hidden"
                    onChange={e => uploadPhotos(Array.from(e.target.files))} disabled={uploading} />
                  {uploading
                    ? <div className="w-5 h-5 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                    : <><ArrowUpTrayIcon className="h-5 w-5 text-gray-400" /><span className="text-xs text-gray-400 mt-1">Upload</span></>
                  }
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-2">Additional images used within the post body.</p>
            </Section>

            {/* ── VIDEO ── */}
            <Section title="Video">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Upload video file */}
                <div>
                  <Label>Upload Video File</Label>
                  {blogForm.videoFile ? (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                      <FilmIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">Video uploaded</p>
                        <p className="text-xs text-gray-400 truncate">{blogForm.videoFile.split('/').pop()?.split('?')[0]}</p>
                      </div>
                      <button type="button" onClick={() => setBlogForm(p => ({ ...p, videoFile: '' }))}
                        className="text-red-400 hover:text-red-600 flex-shrink-0">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      className="flex flex-col items-center justify-center w-full rounded-xl border-2 border-dashed cursor-pointer transition-all"
                      style={{ height: 100, borderColor: uploadingVideo ? '#800020' : '#d1d5db' }}
                      onMouseEnter={e => !uploadingVideo && (e.currentTarget.style.borderColor = '#800020')}
                      onMouseLeave={e => !uploadingVideo && (e.currentTarget.style.borderColor = '#d1d5db')}>
                      <input ref={videoRef} type="file" accept="video/*" className="hidden"
                        onChange={e => e.target.files[0] && uploadVideo(e.target.files[0])}
                        disabled={uploadingVideo} />
                      {uploadingVideo
                        ? <div className="w-6 h-6 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                        : <><FilmIcon className="h-7 w-7 text-gray-300 mb-1" /><p className="text-xs text-gray-400">Click to upload video</p></>
                      }
                    </label>
                  )}
                </div>

                {/* OR embed URL */}
                <div>
                  <Label>Or Embed Video URL <span className="font-normal text-gray-400 normal-case">(YouTube / Vimeo)</span></Label>
                  <input
                    type="url"
                    value={blogForm.videoUrl}
                    onChange={e => setBlogForm(p => ({ ...p, videoUrl: e.target.value, videoFile: e.target.value ? '' : p.videoFile }))}
                    placeholder="https://youtube.com/watch?v=…"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Paste a YouTube or Vimeo link. Overrides uploaded file.</p>
                </div>
              </div>
            </Section>

            {/* ── SEO ── */}
            <Section title="SEO Settings">
              <div className="space-y-4">
                <div>
                  <Label>SEO Title <span className="font-normal text-gray-400 normal-case">(defaults to post title)</span></Label>
                  <input
                    type="text"
                    value={blogForm.seoTitle}
                    onChange={e => setBlogForm(p => ({ ...p, seoTitle: e.target.value }))}
                    placeholder={blogForm.title || 'SEO-optimised title…'}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                  <p className="text-xs text-gray-400 mt-1">{(blogForm.seoTitle || blogForm.title).length}/60 chars recommended</p>
                </div>
                <div>
                  <Label>SEO Meta Description <span className="font-normal text-gray-400 normal-case">(defaults to excerpt)</span></Label>
                  <textarea
                    value={blogForm.seoDesc}
                    onChange={e => setBlogForm(p => ({ ...p, seoDesc: e.target.value }))}
                    rows={2}
                    placeholder={blogForm.excerpt || 'Describe this post for search engines…'}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }}
                  />
                  <p className="text-xs text-gray-400 mt-1">{(blogForm.seoDesc || blogForm.excerpt).length}/160 chars recommended</p>
                </div>
                {/* SEO Preview */}
                {(blogForm.seoTitle || blogForm.title) && (
                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Search Preview</p>
                    <p className="text-blue-700 text-sm font-medium leading-tight mb-0.5 truncate">
                      {blogForm.seoTitle || blogForm.title}
                    </p>
                    <p className="text-green-700 text-xs mb-0.5">powercorefitness.com/blog/{slugify(blogForm.title || 'post-title')}</p>
                    <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">
                      {blogForm.seoDesc || blogForm.excerpt || 'No description set.'}
                    </p>
                  </div>
                )}
              </div>
            </Section>

          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button type="button" onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <div className="flex gap-3">
            {/* Quick save as draft */}
            {blogForm.status !== 'draft' && !editing && (
              <button type="button"
                onClick={() => { setBlogForm(p => ({ ...p, status: 'draft' })); setTimeout(() => document.querySelector('[data-submit]')?.click(), 50); }}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all"
                style={{ borderColor: '#800020', color: '#800020' }}>
                Save as Draft
              </button>
            )}
            <button data-submit onClick={onSubmit} disabled={loading}
              className="px-10 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              {loading ? 'Saving…' : editing ? 'Update Post' : blogForm.status === 'published' ? 'Publish Post' : 'Save Post'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY MODAL
// ═══════════════════════════════════════════════════════════════════════════════
const CategoryModal = ({ categories, onClose, onRefresh }) => {
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(false);

  const COLOR_PRESETS = ['#800020','#1a1a2e','#16a34a','#d97706','#2563eb','#9333ea','#e11d48','#0891b2','#374151'];

  const openAdd  = ()    => setForm({ ...EMPTY_CATEGORY, editId: null });
  const openEdit = (cat) => setForm({ name: cat.name, color: cat.color || '#800020', description: cat.description || '', editId: cat.id });
  const closeForm = ()   => setForm(null);

  const save = async () => {
    if (!form.name.trim()) return toast('error', 'Name required');
    setLoading(true);
    try {
      const payload = {
        name:        form.name.trim(),
        slug:        form.editId ? undefined : slugify(form.name),
        color:       form.color,
        description: form.description.trim(),
        updatedAt:   Date.now(),
      };
      if (form.editId) {
        delete payload.slug;
        await update(ref(database, `blogCategories/${form.editId}`), payload);
        toast('success', 'Updated');
      } else {
        const nr = push(ref(database, 'blogCategories'));
        await set(nr, { ...payload, id: nr.key, slug: slugify(form.name), createdAt: Date.now() });
        toast('success', 'Created');
      }
      closeForm();
      await onRefresh();
    } catch (e) { console.error(e); toast('error', 'Failed'); }
    finally { setLoading(false); }
  };

  const deleteCat = async (cat) => {
    const ok = await Swal.fire({
      title: `Delete "${cat.name}"?`,
      text: 'Posts in this category will become uncategorised.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#800020', confirmButtonText: 'Delete', ...SWAL_Z,
    });
    if (!ok.isConfirmed) return;
    await remove(ref(database, `blogCategories/${cat.id}`));
    toast('success', 'Deleted');
    await onRefresh();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-xl font-bold text-white">Blog Categories</h2>
            <p className="text-white/60 text-sm mt-0.5">Organise your content into categories</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="max-w-3xl mx-auto px-8 py-8">

            {/* Add button */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#800020' }} />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Categories <span className="ml-1 text-gray-400">({categories.length})</span>
                </span>
              </div>
              <button onClick={() => { closeForm(); openAdd(); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white"
                style={{ background: '#800020' }}>
                <PlusIcon className="h-3.5 w-3.5" />
                Add Category
              </button>
            </div>

            {/* Inline add / edit form */}
            <AnimatePresence>
              {form && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="mb-5 p-5 rounded-2xl border-2 border-dashed bg-gray-50 space-y-4"
                  style={{ borderColor: '#800020' }}>

                  <div>
                    <Label>Category Name *</Label>
                    <input autoFocus type="text" value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') closeForm(); }}
                      placeholder="e.g., Nutrition, Fitness Tips…"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 bg-white transition-all"
                      style={{ '--tw-ring-color': '#800020' }}
                    />
                  </div>

                  <div>
                    <Label>Description <span className="font-normal text-gray-400 normal-case">(optional)</span></Label>
                    <input type="text" value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Short description of this category…"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 bg-white transition-all"
                      style={{ '--tw-ring-color': '#800020' }}
                    />
                  </div>

                  <div>
                    <Label>Colour</Label>
                    <div className="flex items-center gap-3 flex-wrap">
                      {COLOR_PRESETS.map(c => (
                        <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                          className="w-7 h-7 rounded-full transition-all hover:scale-110"
                          style={{
                            background: c,
                            outline: form.color === c ? `3px solid ${c}` : 'none',
                            outlineOffset: '2px',
                          }} />
                      ))}
                      <input type="color" value={form.color}
                        onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                        className="w-7 h-7 rounded-full cursor-pointer border-0 p-0 bg-transparent"
                        title="Custom colour" />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={save} disabled={loading}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
                      style={{ background: '#800020' }}>
                      {loading ? 'Saving…' : form.editId ? 'Update' : 'Create'}
                    </button>
                    <button onClick={closeForm}
                      className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all">
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Category list */}
            {categories.length === 0 && !form ? (
              <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-200">
                <FolderIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">No categories yet</p>
                <button onClick={openAdd} className="mt-2 text-sm font-semibold" style={{ color: '#800020' }}>
                  + Create your first category
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {categories.map(cat => (
                  <div key={cat.id}
                    className="flex items-center gap-4 bg-white rounded-2xl border border-gray-100 px-5 py-4 hover:shadow-sm transition-all">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: (cat.color || '#800020') + '20' }}>
                      <div className="w-4 h-4 rounded-full" style={{ background: cat.color || '#800020' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">{cat.name}</p>
                      {cat.description && <p className="text-xs text-gray-400 truncate">{cat.description}</p>}
                      {cat.slug && <p className="text-xs text-gray-300 mt-0.5">/{cat.slug}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => { closeForm(); openEdit(cat); }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-all">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteCat(cat)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <p className="text-sm text-gray-400">{categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</p>
          <button onClick={onClose}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg"
            style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{children}</label>
);

const Section = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <p className="text-base font-bold text-gray-900 whitespace-nowrap">{title}</p>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
    {children}
  </div>
);

export default BlogManagement;